import { useId, type ReactNode } from 'react';

import { cn } from '@core/lib/cn';

export interface CardProps {
  /** Section heading. Rendered as an <h2> and used as the section's label. */
  title?: ReactNode;
  /** Right-aligned header slot, e.g. a Badge. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Removes the body padding for edge-to-edge lists (see IssueCard). */
  flush?: boolean;
}

/**
 * The single-column content container everything sits in.
 *
 * A <section> with a heading rather than a <div>: on a page made of five stacked
 * cards, headings are how a screen-reader user navigates, and a div soup gives
 * them nothing to jump between.
 */
export function Card({ title, aside, children, className, flush = false }: CardProps) {
  const headingId = useId();

  return (
    <section
      // A <section> only exposes the `region` role once it has an accessible name.
      // Without this, a page of five cards is five anonymous groups a screen reader
      // cannot jump between.
      aria-labelledby={title ? headingId : undefined}
      className={cn(
        'overflow-hidden rounded-card border border-line bg-surface shadow-card',
        className,
      )}
    >
      {title ? (
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2
            id={headingId}
            className="text-sm font-semibold uppercase tracking-wide text-ink-muted"
          >
            {title}
          </h2>
          {aside}
        </div>
      ) : null}
      <div className={flush ? '' : 'p-4'}>{children}</div>
    </section>
  );
}

export interface DataRowProps {
  label: ReactNode;
  value: ReactNode;
  /** Emphasises the value — for the order total. */
  emphasis?: boolean;
  className?: string;
}

/**
 * A label/value line. Uses a definition list pairing so the relationship between
 * "Total" and "₹1,234.56" survives being read out of visual order.
 */
export function DataRow({ label, value, emphasis = false, className }: DataRowProps) {
  return (
    <div className={cn('flex items-baseline justify-between gap-4 py-1.5', className)}>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd
        className={cn(
          'text-right tabular-nums',
          emphasis ? 'text-lg font-semibold text-ink' : 'text-sm font-medium text-ink',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
